import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useRef } from "react";
import { withPromptDependencyFields } from "@/features/dynamic-variables/repeatable/prompt-dependencies";
import { Controller, type UseFormReturn } from "react-hook-form";
import type { RepeatableTreeNode } from "@/features/dynamic-variables/repeatable/scope";
import type {
	Repeatable,
	RepeatableField,
} from "@/features/dynamic-variables/repeatable/types";
import { FieldCell } from "./field-cell";
import { RepeatableCsvMenu } from "./repeatable-csv-menu";
import { useRepeatableDefinitions } from "./repeatable-definitions-context";
import { createRepeatableRow, type RepeatableRow } from "./repeatable-row";
import { track } from "@/features/analytics";
import { VARIABLES_EVENTS } from "@features/dynamic-variables/analytics/variables-analytics";

interface Props {
	repeatable: Repeatable;
	form: UseFormReturn<Record<string, unknown>>;
	/** Nested groups whose rows live INSIDE each row of this group under their key. */
	children?: RepeatableTreeNode[];
	/** Row index whose card is labelled "shown in the editor" (pop-up passes 0). */
	editorRowIndex?: number;
	/** Nested key to scroll into view and mark as focused (pop-up passes focusKey). */
	focusKey?: string;
}

/** The rows stored under a nested group's key, or an empty list when unset. */
const asRows = (value: unknown): RepeatableRow[] =>
	Array.isArray(value) ? (value as RepeatableRow[]) : [];

/** Stable identity for "no nested groups", so the blank-row memos below hold. */
const NO_CHILDREN: RepeatableTreeNode[] = [];

/**
 * The rows to RENDER: the stored rows, or a single blank row when the group has
 * none yet.
 *
 * The editor always renders the marked content once, bound to row 1, so a group
 * with no rows must still show row #1 — otherwise the sender is handed a card
 * with nothing but "Add more" and no idea what they are being asked to fill in.
 *
 * The blank row is rendered, NOT written into the form value. An untouched row
 * carries nothing, and persisting it would publish a blank copy of the repeated
 * content, light up Edit version's unsaved-changes gate (`formState.isDirty`),
 * and be clobbered anyway by edit-version's `variableForm.reset(...)` effects,
 * which re-run as the fast-vars queries settle. The first edit commits the row
 * (see `withRowEdited`), and so does "Add more".
 */
const rowsToRender = (
	rows: RepeatableRow[],
	blankRow: RepeatableRow,
): RepeatableRow[] => (rows.length > 0 ? rows : [blankRow]);

/**
 * Rows after editing row `rowIndex` — committing the rendered blank row when
 * the group had nothing stored yet.
 */
const withRowEdited = (
	rows: RepeatableRow[],
	rowIndex: number,
	next: RepeatableRow,
): RepeatableRow[] =>
	rows.length === 0
		? [next]
		: rows.map((row, index) => (index === rowIndex ? next : row));

const RemoveRowButton = ({ onClick }: { onClick: () => void }) => (
	<button
		type="button"
		aria-label="Remove item"
		onClick={onClick}
		className="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-text-blue-500 hover:tw-bg-[#f3f2f5]"
	>
		<IconTrash className="tw-size-4 !tw-fill-none" />
	</button>
);

const AddMoreButton = ({ onClick }: { onClick: () => void }) => (
	<button
		type="button"
		onClick={onClick}
		className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs"
	>
		<IconPlus className="tw-size-4 !tw-fill-none" />
		Add more
	</button>
);

interface RowFieldsProps {
	fields: RepeatableField[];
	row: RepeatableRow;
	groupKey: string;
	parentScope?: Record<string, unknown>;
	onCellChange: (fieldKey: string, value: unknown) => void;
}

/** The label/control grid for one row's own fields. */
const RowFields = ({
	fields,
	row,
	groupKey,
	parentScope,
	onCellChange,
}: RowFieldsProps) => (
	<>
		{fields.map((rowField, fieldIndex) => (
			<div
				key={rowField.key}
				className={`tw-grid tw-grid-cols-2 tw-items-center tw-gap-4 tw-px-2 tw-py-3 ${
					fieldIndex !== fields.length - 1
						? "tw-border-b tw-border-solid tw-border-[#e7e6ec]"
						: ""
				}`}
			>
				<span className="tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue">
					{rowField.name}
				</span>
				<FieldCell
					field={rowField}
					value={row[rowField.key]}
					row={row}
					groupKey={groupKey}
					parentScope={parentScope}
					onChange={(value) => onCellChange(rowField.key, value)}
				/>
			</div>
		))}
	</>
);

interface RowBodyProps {
	fields: RepeatableField[];
	row: RepeatableRow;
	groupKey: string;
	/** Fields of the parent rows (nested groups), nearest last. */
	parentScope?: Record<string, unknown>;
	/** Nested groups whose rows live inside this row under their key. */
	nested: RepeatableTreeNode[];
	focusKey?: string;
	/** Replace this row with a rebuilt copy. */
	onRowChange: (row: RepeatableRow) => void;
}

/** One row's fields followed by a sub-card per nested group. */
const RowBody = ({
	fields,
	row,
	groupKey,
	parentScope,
	nested,
	focusKey,
	onRowChange,
}: RowBodyProps) => (
	<>
		<RowFields
			fields={fields}
			row={row}
			groupKey={groupKey}
			parentScope={parentScope}
			onCellChange={(fieldKey, value) =>
				onRowChange({ ...row, [fieldKey]: value })
			}
		/>
		{nested.map((child) => (
			<NestedRowsCard
				key={child.repeatable.key}
				node={child}
				rows={asRows(row[child.repeatable.key])}
				parentScope={{ ...(parentScope ?? {}), [groupKey]: row, ...row }}
				focusKey={focusKey}
				onChange={(rows) =>
					onRowChange({ ...row, [child.repeatable.key]: rows })
				}
			/>
		))}
	</>
);

interface NestedRowsCardProps {
	node: RepeatableTreeNode;
	rows: RepeatableRow[];
	parentScope?: Record<string, unknown>;
	focusKey?: string;
	onChange: (rows: RepeatableRow[]) => void;
}

/**
 * A nested group's rows, rendered inside the parent row that owns them. Same
 * visual language as the top-level card minus the CSV import (top-level only).
 */
const NestedRowsCard = ({
	node,
	rows,
	parentScope,
	focusKey,
	onChange,
}: NestedRowsCardProps) => {
	const { key, name, fields: declaredFields } = node.repeatable;
	const { definitions } = useRepeatableDefinitions();
	// Groups marked before an AI field's dependencies were registered: derive
	// them here so every row still gets its `industry`-style inputs.
	const fields = useMemo(
		() =>
			withPromptDependencyFields(
				declaredFields,
				definitions,
				key,
				new Set(Object.keys(parentScope ?? {})),
			),
		[declaredFields, definitions, key, parentScope],
	);
	const cardRef = useRef<HTMLDivElement>(null);
	const focused = focusKey === key;
	// Row #1 shows even before anything is stored — see `rowsToRender`.
	const blankRow = useMemo(
		() => createRepeatableRow(fields, node.children, definitions),
		[fields, node.children, definitions],
	);
	const visibleRows = rowsToRender(rows, blankRow);

	useEffect(() => {
		if (!focused) return;
		const card = cardRef.current;
		// jsdom (and older browsers) do not implement scrollIntoView.
		if (card && typeof card.scrollIntoView === "function")
			card.scrollIntoView({ block: "nearest" });
	}, [focused]);

	return (
		<div
			ref={cardRef}
			data-repeatable-key={key}
			data-focus={focused ? "true" : undefined}
			className="tw-mt-3 tw-rounded-lg tw-border tw-border-solid tw-border-black/5 tw-bg-white tw-p-4"
		>
			<div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
				<h4 className="tw-font-sans tw-text-sm tw-font-medium tw-leading-6 tw-text-blue">
					{name}
				</h4>
			</div>
			<div className="tw-flex tw-flex-col tw-gap-3">
				{visibleRows.map((row, rowIndex) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: rows are plain records with no stable id
						key={rowIndex}
						className="tw-rounded-lg tw-border tw-border-solid tw-border-black/5 tw-bg-white tw-p-4"
					>
						<div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
							<span className="tw-font-sans tw-text-sm tw-font-medium tw-leading-6 tw-text-blue">
								#{rowIndex + 1}
							</span>
							<RemoveRowButton
								onClick={() => {
									// Nothing stored yet: the rendered blank row #1 is already empty.
									if (rows.length === 0) return;
									onChange(rows.filter((_, index) => index !== rowIndex));
								}}
							/>
						</div>
						<RowBody
							fields={fields}
							row={row}
							groupKey={key}
							parentScope={parentScope}
							nested={node.children}
							// The editor renders row 1 of every level, so a focus target
							// deeper down belongs to this level's first row only.
							focusKey={rowIndex === 0 ? focusKey : undefined}
							onRowChange={(next) =>
								onChange(withRowEdited(rows, rowIndex, next))
							}
						/>
					</div>
				))}
				<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
					<AddMoreButton
						// `visibleRows`, so adding from the not-yet-stored blank row #1 keeps
						// it and appends #2 instead of silently replacing it.
						onClick={() =>
							onChange([
								...visibleRows,
								createRepeatableRow(fields, node.children, definitions),
							])
						}
					/>
				</div>
			</div>
		</div>
	);
};

export const RepeatableGroupCard = ({
	repeatable,
	form,
	children = NO_CHILDREN,
	editorRowIndex,
	focusKey,
}: Props) => {
	const { key, name, fields: declaredFields } = repeatable;
	const { definitions } = useRepeatableDefinitions();
	// See NestedRowsCard: dependencies of AI fields become row fields too.
	const fields = useMemo(
		() => withPromptDependencyFields(declaredFields, definitions, key),
		[declaredFields, definitions, key],
	);
	// Row #1 shows even before anything is stored — see `rowsToRender`.
	const blankRow = useMemo(
		() => createRepeatableRow(fields, children, definitions),
		[fields, children, definitions],
	);

	return (
		<div className="tw-rounded-lg tw-border tw-border-solid tw-border-black/5 tw-bg-white tw-p-4 tw-shadow-xs">
			<div className="tw-mb-3 tw-flex tw-items-center tw-justify-between">
				<h3 className="tw-font-sans tw-text-sm tw-font-medium tw-leading-6 tw-text-blue">
					{name}
				</h3>
			</div>
			<Controller
				name={key}
				control={form.control}
				render={({ field }) => {
					const rows: RepeatableRow[] = asRows(field.value);
					const visibleRows = rowsToRender(rows, blankRow);

					const updateRow = (rowIndex: number, next: RepeatableRow) =>
						field.onChange(withRowEdited(rows, rowIndex, next));
					// `visibleRows`, so adding from the not-yet-stored blank row #1 keeps it
					// and appends #2 instead of silently replacing it.
					const addRow = () =>
						field.onChange([
							...visibleRows,
							createRepeatableRow(fields, children, definitions),
						]);
					const removeRow = (rowIndex: number) => {
						// Nothing stored yet: the rendered blank row #1 is already empty.
						if (rows.length === 0) return;
						field.onChange(rows.filter((_, index) => index !== rowIndex));
					};
					// Replace-all, but each imported row still needs this group's nested
					// keys seeded — a CSV only carries the group's own field columns.
					const importCsv = (records: Record<string, string>[]) => {
						field.onChange(
							records.map((record) => ({
								...createRepeatableRow(fields, children, definitions),
								...record,
							})),
						);
						track(VARIABLES_EVENTS.SUCCESS_IMPORT_VARIABLES_CSV, {
							target: "repeatable rows",
							"rows count": records.length,
						});
					};

					return (
						<div className="tw-flex tw-flex-col tw-gap-3">
							{visibleRows.map((row, rowIndex) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: rows are plain records with no stable id
									key={rowIndex}
									className="tw-rounded-lg tw-border tw-border-solid tw-border-black/5 tw-bg-white tw-p-4"
								>
									<div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
										<span className="tw-font-sans tw-text-sm tw-font-medium tw-leading-6 tw-text-blue">
											#{rowIndex + 1}
											{editorRowIndex === rowIndex && " · shown in the editor"}
										</span>
										<RemoveRowButton onClick={() => removeRow(rowIndex)} />
									</div>
									<RowBody
										fields={fields}
										row={row}
										groupKey={key}
										nested={children}
										focusKey={
											rowIndex === (editorRowIndex ?? 0) ? focusKey : undefined
										}
										onRowChange={(next) => updateRow(rowIndex, next)}
									/>
								</div>
							))}
							<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
								<AddMoreButton onClick={addRow} />
								<RepeatableCsvMenu
									groupKey={key}
									fields={fields}
									onImport={importCsv}
								/>
							</div>
						</div>
					);
				}}
			/>
		</div>
	);
};
